import { useCallback, useState } from 'react'
import { AuthProvider, AuthGate, InviteAcceptScreen } from '@/features/auth'
import { ConversationsProvider } from '@/features/conversations/ConversationsProvider'
import { ScopeProvider } from '@/features/scope'
import { ReferenceSelectionProvider } from '@/features/references'
import { GuidedIngestionProvider } from '@/features/ingestion'
import { AppStateProvider } from './providers/AppStateProvider'
import { AppShell } from './AppShell'
import { IconSprite } from '@/shared/ui/Icon'
import { ConsentBanner } from '@/shared/consent'

function readInviteToken(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const params = new URLSearchParams(window.location.search)
    const t = params.get('invite') || params.get('token')
    if (t && t.trim().length >= 8) return t.trim()
  } catch {
    // ignored
  }
  if (window.location.pathname.startsWith('/accept-invite')) {
    const params = new URLSearchParams(window.location.search)
    return params.get('token')
  }
  return null
}

export default function App() {
  // STEP 9.2 — invite acceptance happens BEFORE the auth gate so an
  // unauthenticated browser can land directly on the screen via a shared
  // ``?invite=<token>`` URL without bouncing through the login page first.
  const [inviteToken, setInviteToken] = useState<string | null>(() =>
    readInviteToken(),
  )

  const clearInvite = useCallback(() => {
    setInviteToken(null)
    if (typeof window !== 'undefined') {
      // Drop the token from the URL so a refresh does not reopen the flow.
      try {
        const url = new URL(window.location.href)
        url.searchParams.delete('invite')
        url.searchParams.delete('token')
        const next = url.pathname.startsWith('/accept-invite')
          ? '/'
          : url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '')
        window.history.replaceState({}, '', next)
      } catch {
        // ignored
      }
    }
  }, [])

  return (
    <AuthProvider>
      <IconSprite />
      {/* LGPD Block B — consent banner sits at the root so it covers the
          login, invite and authenticated surfaces alike. It only reads/writes
          localStorage; no script is loaded by it. */}
      <ConsentBanner />
      {inviteToken ? (
        <InviteAcceptScreen token={inviteToken} onDone={clearInvite} />
      ) : (
        <AuthGate>
          <ScopeProvider>
            {/* STEP 8.1 — Reference Selection for Queries.
                ReferenceSelectionProvider lives ABOVE ConversationsProvider so
                the chat sender can read selectedIds when posting a message, but
                BELOW ScopeProvider since the two are independent: the case
                scope and the reference set are different orthogonal axes. */}
            <ReferenceSelectionProvider>
              <ConversationsProvider>
                <AppStateProvider>
                  {/* Guided ingestion (STEP 7.10) lives below AppStateProvider so the
                      dialog can flip ``activeWorkspace`` back to "chat" after a
                      successful upload, and below ConversationsProvider so it can
                      call ``startNewConsultation`` to land the user on a fresh prompt
                      with the new scope locked in. */}
                  <GuidedIngestionProvider>
                    <AppShell />
                  </GuidedIngestionProvider>
                </AppStateProvider>
              </ConversationsProvider>
            </ReferenceSelectionProvider>
          </ScopeProvider>
        </AuthGate>
      )}
    </AuthProvider>
  )
}
